import { useRef, type ReactNode, type RefObject } from 'react';
import { useTrackUnsaved } from '@/stores/unsaved';
import { AppIcon, type IconKind } from './AppIcon';
import { SplitView } from './desktop';
import { UnsavedFormContext, useGuardedCancel } from './unsavedForm';

/**
 * The frame a document's create/edit form now uses instead of a modal: the
 * fields that describe what is being added sit in a top pane, the running
 * list of what has been added — and the Save actions that commit it — sit in
 * a bottom pane, with a draggable divider between them so either can have
 * more room. Replaces the old popup because staging several items before
 * saving needs somewhere to actually see them while still adding more.
 *
 * A form whose every field sits on its lines (Receive Stock) has no top pane:
 * the list takes the whole height, with no empty pane or divider above it.
 */
export function ComposePanel({
  title,
  intro,
  icon,
  onCancel,
  cancelLabel = 'Back to list',
  top,
  bottom,
  footer,
  splitId,
}: {
  title: string;
  intro?: string;
  /** The document type's icon, drawn before the title. */
  icon?: IconKind;
  onCancel: () => void;
  cancelLabel?: string;
  /** Left out, there is no top pane and no divider: `bottom` fills the frame. */
  top?: ReactNode;
  bottom: ReactNode;
  /** Pinned under the scrolling list: the notes and Save actions stay in view. */
  footer?: ReactNode;
  splitId: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const formId = useTrackUnsaved(ref);
  return (
    <UnsavedFormContext.Provider value={formId}>
      <ComposeFrame
        {...{ title, intro, icon, onCancel, cancelLabel, top, bottom, footer, splitId }}
        frameRef={ref}
      />
    </UnsavedFormContext.Provider>
  );
}

/** Inside the provider, so its Cancel can see whether the form has unsaved work. */
function ComposeFrame({
  title,
  intro,
  icon,
  onCancel,
  cancelLabel,
  top,
  bottom,
  footer,
  splitId,
  frameRef,
}: {
  title: string;
  intro?: string | undefined;
  icon?: IconKind | undefined;
  onCancel: () => void;
  cancelLabel: string;
  top?: ReactNode;
  bottom: ReactNode;
  footer?: ReactNode | undefined;
  splitId: string;
  frameRef: RefObject<HTMLDivElement | null>;
}) {
  const cancel = useGuardedCancel(onCancel);
  const review = (
    <>
      <div className="composepanel__scroll">{bottom}</div>
      {footer && <div className="composepanel__footer">{footer}</div>}
    </>
  );
  return (
    <div className="workbench" ref={frameRef}>
      <div className="composepanel__head">
        <div>
          <h2 className={icon ? 'withicon' : undefined}>
            {icon && <AppIcon kind={icon} size={18} />}
            {title}
          </h2>
          {intro && <p className="muted formgroup__hint">{intro}</p>}
        </div>
        <button type="button" className="btn btn--ghost" onClick={cancel}>
          {cancelLabel}
        </button>
      </div>
      {top === undefined ? (
        <div className="composepanel__pane composepanel__pane--review composepanel__single">{review}</div>
      ) : (
        <SplitView
          id={splitId}
          direction="column"
          initial={300}
          firstLabel="add entries"
          first={<div className="composepanel__pane">{top}</div>}
          second={<div className="composepanel__pane composepanel__pane--review">{review}</div>}
        />
      )}
    </div>
  );
}
